import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DangerZone,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
} from '@intelliauth/ui'
import { AlertTriangle, Loader2, Settings } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, cpClient } from '@/lib/api/cpClient'

function readErr(err: unknown, fallback: string): string {
  if (err instanceof CpV2ApiError) return err.message
  if (err instanceof Error) return err.message
  return fallback
}

export default function SettingsPage() {
  const { currentOrg, refreshSession, logout } = useAuth()

  // ── General Settings ──────────────────────────────────────────
  const [name, setName] = useState('')
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (currentOrg) setName(currentOrg.name)
  }, [currentOrg])

  const handleSaveName = async () => {
    if (!currentOrg || !name.trim() || name === currentOrg.name) return
    setSaving(true)
    try {
      await cpClient().patch('/api/v1/organizations/{id}', {
        path: { id: currentOrg.id },
        body: { name: name.trim() },
      })
      await refreshSession()
      toast.success('Organization name updated')
    } catch (err) {
      toast.error(readErr(err, 'Failed to update name'))
    } finally {
      setSaving(false)
    }
  }

  // ── Danger Zone ───────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [confirmSlug, setConfirmSlug] = useState('')
  const [deleting, setDeleting] = useState(false)

  const handleDelete = async () => {
    if (!currentOrg || confirmSlug !== currentOrg.slug) return
    setDeleting(true)
    try {
      await cpClient().delete('/api/v1/organizations/{id}', {
        path: { id: currentOrg.id },
      })
      toast.success('Organization deletion initiated. You will receive email updates on progress.')
      setTimeout(() => logout(), 500)
    } catch (err) {
      const message = readErr(err, 'Failed to delete organization')
      if (message.includes('already being deleted')) {
        toast.error('This organization is already being deleted.')
      } else {
        toast.error(message)
      }
    } finally {
      setDeleting(false)
    }
  }

  if (!currentOrg) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/70" />
      </div>
    )
  }

  return (
    <div>
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <Settings className="h-6 w-6 text-muted-foreground" />
          <h1 className="text-2xl font-semibold text-foreground tracking-tight">
            Organization Settings
          </h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">
          Manage your organization's general settings.
        </p>
      </div>

      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="text-base">General</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label htmlFor="org-name" className="block text-sm font-medium text-foreground mb-1.5">
              Organization name
            </label>
            <div className="flex gap-3">
              <Input
                id="org-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 max-w-sm"
                placeholder="Organization name"
              />
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveName}
                disabled={!name.trim() || name === currentOrg.name}
                loading={saving}
              >
                Save
              </Button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Slug</label>
            <div className="flex items-center gap-2 max-w-sm">
              <Input
                type="text"
                value={currentOrg.slug}
                disabled
                className="flex-1 bg-background text-muted-foreground cursor-not-allowed"
              />
            </div>
            <p className="mt-1 text-xs text-muted-foreground/70">
              The slug cannot be changed after creation.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary capitalize">
              {currentOrg.role}
            </span>
            <p className="mt-1 text-xs text-muted-foreground/70">
              Your role in this organization. Plan management is coming soon.
            </p>
          </div>
        </CardContent>
      </Card>

      <DangerZone
        className="mb-8"
        title="Delete this organization"
        description="Once deleted, all data associated with this organization will be permanently lost. This action cannot be undone."
        action={
          <Button variant="destructive" size="sm" onClick={() => setShowDeleteModal(true)}>
            Delete organization
          </Button>
        }
      />

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowDeleteModal(false)
            setConfirmSlug('')
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-500" />
              </div>
              <div>
                <DialogTitle>Delete organization</DialogTitle>
                <DialogDescription>This action cannot be undone.</DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">
            This will permanently delete{' '}
            <strong className="text-foreground">{currentOrg.name}</strong> and tear down all
            associated infrastructure including tenants, member accounts, and permissions. You will
            be logged out immediately and receive email updates as the process completes.
          </p>
          <p className="text-sm text-foreground mb-2">
            Type{' '}
            <code className="px-1.5 py-0.5 bg-muted rounded text-red-600 font-mono text-xs">
              {currentOrg.slug}
            </code>{' '}
            to confirm:
          </p>
          <Input
            type="text"
            value={confirmSlug}
            onChange={(e) => setConfirmSlug(e.target.value)}
            className="w-full mb-4"
            placeholder={currentOrg.slug}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowDeleteModal(false)
                setConfirmSlug('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleDelete}
              disabled={confirmSlug !== currentOrg.slug}
              loading={deleting}
            >
              Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
